"use client";
import { useState, useCallback, useMemo, useEffect } from "react";
import MealPlanFilter from "@/components/hotel-listing-v2/ui/Filters/MealPlanFilter";
import AmenitiesFilter from "@/components/hotel-listing-v2/ui/Filters/AmenitiesFilter";
import RatingFilter from "@/components/hotel-listing-v2/ui/Filters/RatingFilter";
import PriceSlider from "@/components/hotel-listing-v2/ui/Filters/PriceSlider";
import PropertySearch from "@/components/hotel-listing-v2/ui/Filters/PropertySearch";
import PropertyTypeFilter from "@/components/hotel-listing-v2/ui/Filters/PropertyTypeFilter";
import HotelsMap from "@/components/hotel-listing-v2/ui/Map/HotelsMap";
import { getUserCurrency } from "@/lib/features/home/homeSlice";
import {
  Filter,
  getHotelAppliedFilters,
  loading,
  setHotelAppliedFilters,
} from "@/lib/features/hotel/hotelSlice";
import { useAppSelector, useAppDispatch } from "@/lib/hooks";
import { isEqual } from "lodash-es";
import { ModalHeader, ModalBody, Modal } from "reactstrap";
import { useRouter, useSearchParams } from "next/navigation";
import { makeQueryString, parseQueryString } from "@/utils/queryString";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { transformMealPlanOptions } from "@/utils/mealPlanUtils";
export const SideBarFilters = ({
  hotelCode,
  onFilter,
  onSearch,
  allAmenities,
  allMealPlan,
  hotels,
  propertyTypes,
}: {
  hotelCode: string | number;
  onFilter: (payload: Filter[]) => void;
  onSearch: boolean;
  allAmenities: any;
  allMealPlan: any;
  hotels: any;
  propertyTypes: any;
}) => {
  const router = useRouter();
  const queryParams: any = useSearchParams();

  // Parse the query string into a payload using parseQueryString
  const payloadFromQuery: any = useMemo(() => {
    if (!queryParams) return null; // Handle case where searchParams isn't ready
    return parseQueryString(queryParams);
  }, [queryParams]);

  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  const toggleMapModal = () => setIsMapModalOpen(!isMapModalOpen);

  const amenities = allAmenities;
  const mealPlans = allMealPlan;
  const hotelId: any = Number(hotelCode);

  const appliedFilters = useAppSelector((state) =>
    getHotelAppliedFilters(state, hotelId)
  );

  const currency = useAppSelector(getUserCurrency);
  const isLoading = useAppSelector(loading);
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<any>({});

  const [searchTerm, setSearchTerm] = useState("");
  const [hotelName, setHotelName] = useState<any>({
    filterType: "hotel_name",
    title: "Hotel name",
    value: searchTerm,
  });
  // Define specific types for each filter's value
  // Define filter values with proper typing
  const priceValue = useMemo(
    () =>
      (appliedFilters?.length > 0 &&
        (appliedFilters?.find((f: Filter) => f.filterType === "price")
          ?.value as { from: number; to: number; max?: number })) || {
        from: currency?.min || 0,
        to: currency?.max || 700,
      },
    [appliedFilters, currency]
  );
  const amenitiesValue = useMemo(
    () =>
      (appliedFilters?.length > 0 &&
        (appliedFilters?.find((f: Filter) => f.filterType === "amenities")
          ?.value as string[])) ||
      [],
    [appliedFilters]
  );
  const mealPlan = useMemo(
    () =>
      (appliedFilters?.length > 0 &&
        (appliedFilters?.find((f: Filter) => f.filterType === "meal_plan")
          ?.value as string[])) ||
      [],
    [appliedFilters]
  );
  const ratingValue = useMemo(
    () =>
      (appliedFilters?.length > 0 &&
        (appliedFilters?.find((f: Filter) => f.filterType === "rating")
          ?.value as number[])) ||
      [],
    [appliedFilters]
  );
  const sortByValue = useMemo(
    () =>
      appliedFilters?.length > 0 &&
      (appliedFilters?.find((f: Filter) => f.filterType === "sortBy")
        ?.value as { label: string; value: string }),
    // {
    //   label: "Star Rating",
    //   value: "rating_desc",
    // }
    [appliedFilters]
  );

  const propertyTypeValue = useMemo(
    () =>
      (appliedFilters?.length > 0 &&
        (appliedFilters?.find((f: Filter) => f.filterType === "property_type")
          ?.value as string[])) ||
      [],
    [appliedFilters]
  );

  /*const updateFilters = useCallback(
    (filterType: string, value: any) => {
      const newFilter: Filter = { filterType, title: filterType, value };
      const currentFilters = appliedFilters || [];
      const updatedFilters = [
        ...currentFilters.filter((f: Filter) => f.filterType !== filterType),
        newFilter,
      ];

      // Only dispatch if the filters have actually changed
      console.log("updatedFilters", updatedFilters, !isEqual(updatedFilters, currentFilters));
      if (!isEqual(updatedFilters, currentFilters)) {
        const filterPayload: any = {
          hotel_code: hotelCode,
          filters: updatedFilters,
        };
        console.log("Dispatching filters")
        dispatch(setHotelAppliedFilters(filterPayload));
        const filteredData = updatedFilters.filter(
          (item: Filter) =>
            item.value && (typeof item.value === "object" || Array.isArray(item.value))
        );
        onFilter(filteredData);
      }
    },
    [appliedFilters, hotelCode, dispatch, onFilter]
  );*/
  const updateFilters = useCallback(
    (filterType: string, value: any) => {
      const currentFilters = appliedFilters || [];
      let updatedFilters: Filter[] = currentFilters.filter(
        (f: Filter) => f.filterType !== filterType
      );

      // Only add the new filter if the value is not an empty array
      if (!Array.isArray(value) || value.length > 0) {
        const newFilter: Filter = { filterType, title: filterType, value };
        updatedFilters = [...updatedFilters, newFilter];
      }

      // Only dispatch if the filters have actually changed
      if (!isEqual(updatedFilters, currentFilters)) {
        const filterPayload: any = {
          hotel_code: hotelCode,
          filters: updatedFilters,
        };
        dispatch(setHotelAppliedFilters(filterPayload));
        const filteredData = updatedFilters.filter(
          (item: Filter) =>
            item.value &&
            (typeof item.value === "object" || Array.isArray(item.value))
        );
        onFilter(filteredData);
      }
    },
    [appliedFilters, hotelCode, dispatch, onFilter]
  );

  const handlePriceValueChange = useCallback(
    (value: { min: number; max: number }, currency: any) => {
      updateFilters("price", {
        from: value.min,
        to: value.max,
        max: currency?.max,
      });
    },
    [updateFilters]
  );

  const handleAmenitesValueChange = useCallback(
    (value: string[]) => {
      updateFilters("amenities", value);
    },
    [updateFilters]
  );

  const handleMeanPlanChange = useCallback(
    (value: string[]) => {
      updateFilters("meal_plan", value);
    },
    [updateFilters]
  );

  const handleRatingChange = useCallback(
    (value: number[]) => {
      updateFilters("rating", value);
    },
    [updateFilters]
  );

  const handleSortByChange = useCallback(
    (value: { label: string; value: string }) => {
      updateFilters("sortBy", value);
    },
    [updateFilters]
  );

  const handlePropertyTypeChange = useCallback(
    (value: string[]) => {
      updateFilters("property_type", value);
    },
    [updateFilters]
  );

  if (
    onSearch &&
    !appliedFilters?.some((f: Filter) => f.filterType === "price")
  ) {
    handlePriceValueChange(
      { min: currency?.min || 0, max: currency?.max || 700 },
      currency
    );
  }

  const options = useMemo(
    () => [
      { label: "Select", value: "" },
      { label: "Price: low to high", value: "price_asc" },
      { label: "Price: high to low", value: "price_desc" },
      { label: "Star Rating", value: "rating_desc" },
    ],
    []
  );

  const onPopupClose = useCallback(() => {
    const payload = payloadFromQuery;
    console.log("payload", payload);
    if (
      payload?.geolocation == "true" &&
      payload?.geolocation != undefined &&
      payload?.north != ""
    ) {
      const req: any = {
        ...payload,
        geolocation: undefined,
        radius: undefined,
        north: undefined,
        east: undefined,
        south: undefined,
        west: undefined,
      };
      const queryString = makeQueryString(req);
      router.push(`/hotels?${queryString}`);
    }
  }, [payloadFromQuery, dispatch]);

  useEffect(() => {
    console.log("isMapModalOpen", isMapModalOpen);
    if (isMapModalOpen == false) {
      onPopupClose();
    }
  }, [isMapModalOpen]);

  // Transform the meal plan options
  const allMealPlanOptions = transformMealPlanOptions(mealPlans);
  
  return (
    <>
      {/* {JSON.stringify(hotels)} */}
      <div className="sidebar__item -no-border position-relative">
        <div
          className="flex-center borderradius-40"
          style={{
            backgroundImage: "url(/img/map-view.svg)",
            height: "150px",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
          }}
        >
          {isLoading ? (
            <p>Loading Map...</p>
            // <Skeleton width={250} height={10} className="button py-15 px-24 -blue-1 bg-white text-dark-1 absolute"/>
          ) : (
            <button
              className="button py-15 px-24 -blue-1 bg-white text-dark-1 absolute"
              data-x-click="mapFilter"
              onClick={toggleMapModal}
              disabled={
                isLoading ||
                !hotels ||
                Object.keys(hotels).length === 0 ||
                hotels?.hotels?.length === 0
              }
            >
              <i className="icon-destination text-22 mr-10" />
              Show on map
            </button>
          )}
        </div>
        <Modal
          isOpen={isMapModalOpen}
          toggle={toggleMapModal}
          // size="xl"
          centered
          fullscreen
          className="hotels-map-modal"
        >
          <ModalHeader toggle={toggleMapModal} className="bg-light">
            {payloadFromQuery?.location?.name},{" "}
            {payloadFromQuery?.location?.label}
          </ModalHeader>
          <ModalBody className="p-0">
            {/* {JSON.stringify(hotels?.length)} */}
            <HotelsMap
              hotels={hotels}
              initialLocation={{ lat: 37.7749, lng: -122.4194 }}
            />
          </ModalBody>
        </Modal>
      </div>
      {/* End find map */}
      {/* {JSON.stringify(appliedFilters)} */}
      <div className="sidebar__item">
        <h5 className="text-18 fw-500 mb-10">Sort By</h5>
        {/* <SearchBox /> */}
        <select
          name="sortBy"
          value={(sortByValue && sortByValue?.value) || ""}
          onChange={(e) =>
            handleSortByChange({
              label: e.target.selectedOptions[0].text,
              value: e.target.value,
            })
          }
          disabled={isLoading}
        >
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="sidebar__item">
        <h5 className="text-18 fw-500 mb-10">Search by property name</h5>
        {/* <SearchBox /> */}
        <PropertySearch />
      </div>
      {/* End search box */}

      <div className="sidebar__item">
        <h5 className="text-18 fw-500 mb-10">Meal Plan</h5>
        <div className="sidebar-checkbox">
          <div className="row y-gap-5 items-center">
            <MealPlanFilter
              onChange={handleMeanPlanChange}
              data={allMealPlanOptions}
              value={mealPlan}
            />
          </div>
        </div>
      </div>
      {/* End deals filter */}

      <div className="sidebar__item">
        <h5 className="text-18 fw-500 mb-10">Amenities</h5>
        <div className="sidebar-checkbox">
          <AmenitiesFilter
            onChange={handleAmenitesValueChange}
            data={amenities}
            value={amenitiesValue}
          />
        </div>
        {/* End Sidebar-checkbox */}
      </div>
      {/* End popular filter */}

      {/* {JSON.stringify(propertyTypes)} */}
      <div className="sidebar__item">
        <h5 className="text-18 fw-500 mb-10">Property Type</h5>
        <div className="sidebar-checkbox">
          <PropertyTypeFilter
            onChange={handlePropertyTypeChange}
            data={propertyTypes}
            value={propertyTypeValue}
          />
        </div>
        {/* End Sidebar-checkbox */}
      </div>

      <div className="sidebar__item pb-30">
        <h5 className="text-18 fw-500 mb-10">Price</h5>
        <div className="row x-gap-10 y-gap-30">
          <div className="col-12">
            <PriceSlider
              key={1}
              onPriceChange={handlePriceValueChange}
              onSearch={onSearch}
              value={{ min: priceValue.from, max: priceValue.to }}
            />
          </div>
        </div>
      </div>
      {/* End Nightly priceslider */}

      <div className="sidebar__item">
        <h5 className="text-18 fw-500 mb-10">Star Rating</h5>
        <div className="row x-gap-10 y-gap-10 pt-10">
          <RatingFilter
            onRatingChange={handleRatingChange}
            value={ratingValue}
          />
        </div>
      </div>
      {/* End rating filter */}

      {/* <div className="sidebar__item">
        <h5 className="text-18 fw-500 mb-10">Guest Rating</h5>
        <div className="sidebar-checkbox">
          <GuestRatingFilters />
        </div>
      </div> */}
      {/* End Guest Rating */}

      {/* <div className="sidebar__item">
        <h5 className="text-18 fw-500 mb-10">Style</h5>
        <div className="sidebar-checkbox">
          <StyleFilter />
        </div>
      </div>
    

      <div className="sidebar__item">
        <h5 className="text-18 fw-500 mb-10">Neighborhood</h5>
        <div className="sidebar-checkbox">
          <NeighborhoddFilter />
        </div>
      </div> */}
      {/* End Aminities filter */}
    </>
  );
};
